import {
  AlertDialog,
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Skeleton,
  Spinner,
  Theme,
} from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import { useResetStoreProofRequirements } from "../../../state/store-proof-requirements";
import { injectExifData } from "../../../lib/exif";

type ProofTarget = "store" | "product";

interface CaptureState {
  target: ProofTarget;
  store_id?: number;
  store_name?: string;
  store_activity_id?: number;
  product_requirement_id?: number;
  product_requirement_label?: string;
  product_requirement_code?: string;
  product_name?: string;
  // Fresh position passed from StoreCard (R22)
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}

function getInstructions(
  state: CaptureState | null,
  t: (key: string) => string,
): string {
  if (!state) return t("proofing.instructions.storeFront");

  if (state.target === "store") {
    return t("proofing.instructions.storeFront");
  }

  // For product proofs, use the dynamic label from the requirement template
  if (state.product_requirement_label) {
    return state.product_requirement_label;
  }

  // Fallback: generic product instruction
  return t("proofing.instructions.storeFront");
}

export function ProofCapture() {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { storeId: storeIdStr } = useParams<{ storeId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);

  const resetStoreRequirements = useResetStoreProofRequirements();

  // Get capture params from location state
  const captureState = location.state as CaptureState | null;
  const storeId = storeIdStr ? Number(storeIdStr) : undefined;

  // Use fresh position from route state (set by StoreCard on capture click)
  const geolocation =
    captureState?.latitude != null && captureState?.longitude != null
      ? {
          latitude: captureState.latitude,
          longitude: captureState.longitude,
          accuracy: captureState.accuracy ?? 0,
        }
      : null;

  // Start camera on mount
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setCameraDenied(false);
      } catch (error) {
        console.error("Error accessing camera:", error);
        if (
          error instanceof DOMException &&
          (error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError")
        ) {
          setCameraDenied(true);
        }
      }
    };

    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Re-attach stream to video element when returning from verification
  useEffect(() => {
    if (!verifying && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      // Camera is already ready if we have a stream
      setCameraReady(true);
    }
  }, [verifying]);

  const handleClose = () => {
    // Navigate back to list (overlay closes, list stays mounted)
    navigate("/proofing");
  };

  const handleTakeCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    try {
      const context = canvasRef.current.getContext("2d");
      if (!context) throw new Error("Could not get canvas context");

      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0);

      const imageDataUrl = canvasRef.current.toDataURL("image/jpeg", 0.9);
      setCapturedImage(imageDataUrl);
      setVerifying(true);
    } catch (error) {
      console.error("Error capturing image:", error);
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setVerifying(false);
  };

  const handleUpload = async () => {
    if (!capturedImage || !canvasRef.current || !captureState) return;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Inject EXIF data (GPS + timestamp) into the image
      const imageWithExif = injectExifData(
        capturedImage,
        geolocation?.latitude || 0,
        geolocation?.longitude || 0,
        new Date(),
      );

      // Convert data URL to blob
      const response = await fetch(imageWithExif);
      const blob = await response.blob();

      // Generate filename based on target
      const timestamp = Date.now();
      const fileName =
        captureState.target === "store"
          ? `proofs/store-${captureState.store_id}-${timestamp}.jpg`
          : `proofs/product-${captureState.store_activity_id}-${captureState.product_requirement_code || captureState.product_requirement_id}-${timestamp}.jpg`;

      const { data, error: uploadError } = await supabase.storage
        .from("proof-images")
        .upload(fileName, blob, {
          contentType: "image/jpeg",
        });

      if (uploadError) {
        console.error("Error uploading image:", uploadError);
        setErrorMessage(`Failed to upload image: ${uploadError.message}`);
        setIsLoading(false);
        return;
      }

      // Insert proof using the insert_proof function
      const { error: insertError } = await supabase.rpc("insert_proof", {
        p_target: captureState.target,
        p_image_url: data.path,
        p_lon: geolocation?.longitude || 0,
        p_lat: geolocation?.latitude || 0,
        p_accuracy: Math.round(geolocation?.accuracy || 0),
        // Store proof params
        p_store_id:
          captureState.target === "store" ? captureState.store_id : undefined,
        // Product proof params
        p_store_activity_id:
          captureState.target === "product"
            ? captureState.store_activity_id
            : undefined,
        p_product_requirement_id:
          captureState.target === "product"
            ? captureState.product_requirement_id
            : undefined,
      });

      if (insertError) {
        console.error("Error inserting proof:", insertError);
        setErrorMessage(`Failed to save proof: ${insertError.message}`);
        setIsLoading(false);
        return;
      }

      // Reset ONLY the store requirements cache to force refetch (not the whole list)
      if (storeId) {
        resetStoreRequirements(storeId);
      }

      // Close overlay and return to list (no success dialog)
      setIsLoading(false);
      navigate("/proofing");
    } catch (error) {
      console.error("Error uploading:", error);
      const message = error instanceof Error ? error.message : String(error);
      setErrorMessage(`Unexpected error: ${message}`);
      setIsLoading(false);
    }
  };

  const instructions = getInstructions(captureState, t);

  return (
    <Theme appearance="dark">
      <Box
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: "var(--black-a11)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {/* Close button */}
        <Button
          size="2"
          variant="ghost"
          onClick={handleClose}
          disabled={isLoading}
          highContrast
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
          }}
        >
          <Cross2Icon width="20" height="20" />
        </Button>

        <Heading style={{ color: "var(--white-a12)" }}>
          {verifying
            ? t("proofing.capture.verifyTitle")
            : t("proofing.instructions.title")}
        </Heading>
        <Text style={{ color: "var(--white-a12)", textAlign: "center" }}>
          {verifying ? t("proofing.capture.verifyMessage") : instructions}
        </Text>
        <Box
          style={{
            width: "100%",
            aspectRatio: "1",
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          {verifying && capturedImage ? (
            <img
              ref={imageRef}
              src={capturedImage}
              alt="Captured"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <>
              {!cameraReady && (
                <Skeleton
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              )}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                onLoadedMetadata={() => setCameraReady(true)}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: cameraReady ? "block" : "none",
                }}
              />
            </>
          )}
        </Box>
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {verifying ? (
          <Flex gap="3" style={{ width: "100%" }}>
            <Button
              size="3"
              style={{ flex: 1 }}
              onClick={handleRetake}
              disabled={isLoading}
              variant="soft"
              highContrast
            >
              {t("proofing.capture.retake")}
            </Button>
            <Button
              size="3"
              style={{ flex: 1 }}
              onClick={handleUpload}
              disabled={isLoading}
              highContrast
            >
              {isLoading ? (
                <>
                  <Spinner size="1" /> {t("proofing.capture.uploading")}
                </>
              ) : (
                t("proofing.capture.upload")
              )}
            </Button>
          </Flex>
        ) : cameraReady ? (
          <Button
            size="3"
            style={{ width: "100%" }}
            onClick={handleTakeCapture}
            disabled={isLoading}
            highContrast
          >
            {t("proofing.capture.takeCapture")}
          </Button>
        ) : null}

        {/* Error dialog - kept unchanged */}
        <AlertDialog.Root
          open={!!errorMessage}
          onOpenChange={(open) => !open && setErrorMessage(null)}
        >
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>
              {t("proofing.errors.uploadTitle")}
            </AlertDialog.Title>
            <AlertDialog.Description size="2">
              {errorMessage}
            </AlertDialog.Description>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Action>
                <Button onClick={() => setErrorMessage(null)}>
                  {t("proofing.errors.close")}
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        {/* Camera permission denied modal */}
        <AlertDialog.Root open={cameraDenied}>
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>
              {t("proofing.cameraDenied.title")}
            </AlertDialog.Title>
            <AlertDialog.Description size="2">
              {t("proofing.cameraDenied.message")}
            </AlertDialog.Description>
            <Text size="1" color="gray" mt="2">
              {t("proofing.cameraDenied.instructions")}
            </Text>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" onClick={handleClose}>
                  {t("proofing.cameraDenied.goBack")}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button onClick={() => window.location.reload()}>
                  {t("proofing.cameraDenied.reloadPage")}
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Box>
    </Theme>
  );
}
